import React, { useState, useEffect, useCallback } from 'react';
import { emailTemplatesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Mail,
  Palette,
  RotateCcw,
  Save,
  Eye,
  Loader2,
  CheckCircle,
  Pencil,
  Code,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const VAR_DESCRIPTIONS = {
  client_name: 'Client full name',
  case_name: 'Case display name',
  org_name: 'Organization name',
  email: 'Client email address',
  access_code: 'Portal access code',
  portal_url: 'Client portal URL',
  old_status: 'Previous case status',
  new_status: 'New case status',
  notes: 'Status change notes',
  filename: 'Uploaded filename',
  uploaded_at: 'Upload timestamp',
  days_until: 'Days until appointment',
  biometrics_date: 'Biometrics date',
  user_name: 'User display name',
  reset_code: 'Password reset code',
  meeting_title: 'Meeting title',
  meeting_date: 'Meeting date',
  meeting_time: 'Meeting time',
  meeting_location: 'Meeting location',
  updated_at: 'Update timestamp',
  fields_html: 'HTML list of updated fields',
  case_id: 'Case identifier',
};

function TemplateCard({ template, onEdit }) {
  return (
    <Card
      className="hover:border-primary/40 transition-colors cursor-pointer group"
      onClick={() => onEdit(template)}
      data-testid={`template-card-${template.template_type}`}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-sm font-semibold text-foreground">{template.name}</h3>
          </div>
          {template.is_customized ? (
            <Badge className="bg-amber-100 text-amber-800 text-xs">Customized</Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">Default</Badge>
          )}
        </div>
        <p className="font-body text-xs text-muted-foreground mb-3 line-clamp-1">
          Subject: {template.subject}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full border" style={{ background: template.primary_color }} />
            <div className="w-4 h-4 rounded-full border" style={{ background: template.accent_color }} />
          </div>
          <span className="font-body text-xs text-muted-foreground">
            {template.variables.length} variables
          </span>
          <Pencil className="w-3 h-3 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </CardContent>
    </Card>
  );
}

function EditPanel({ template, onSave, onReset, onClose }) {
  const [form, setForm] = useState({
    subject: template.subject,
    header_title: template.header_title,
    greeting: template.greeting,
    body_text: template.body_text,
    button_text: template.button_text,
    footer_text: template.footer_text,
    primary_color: template.primary_color,
    accent_color: template.accent_color,
  });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await emailTemplatesAPI.update(template.template_type, form);
      onSave(res.data);
      toast.success('Template saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await emailTemplatesAPI.reset(template.template_type);
      const d = res.data;
      setForm({
        subject: d.subject, header_title: d.header_title, greeting: d.greeting,
        body_text: d.body_text, button_text: d.button_text, footer_text: d.footer_text,
        primary_color: d.primary_color, accent_color: d.accent_color,
      });
      onReset(d);
      toast.success('Template reset to default');
    } catch {
      toast.error('Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await emailTemplatesAPI.preview(template.template_type, form);
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground">{template.name}</h2>
            <p className="font-body text-sm text-muted-foreground">{template.template_type}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePreview} disabled={previewLoading} data-testid="preview-template-btn">
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
              Preview
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>

        {/* Variables reference */}
        <Card className="border-dashed">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Code className="w-4 h-4 text-muted-foreground" />
              <span className="font-body text-xs font-semibold text-muted-foreground uppercase tracking-wider">Available Variables</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {template.variables.map(v => (
                <span
                  key={v}
                  className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-xs font-mono text-slate-700 cursor-help"
                  title={VAR_DESCRIPTIONS[v] || v}
                >
                  {`{{${v}}}`}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <Label className="font-body text-sm">Subject Line</Label>
              <Input value={form.subject} onChange={e => handleChange('subject', e.target.value)} className="font-body mt-1" data-testid="template-subject" />
            </div>
            <div>
              <Label className="font-body text-sm">Header Title</Label>
              <Input value={form.header_title} onChange={e => handleChange('header_title', e.target.value)} className="font-body mt-1" data-testid="template-header-title" />
            </div>
            <div>
              <Label className="font-body text-sm">Greeting</Label>
              <Input value={form.greeting} onChange={e => handleChange('greeting', e.target.value)} className="font-body mt-1" data-testid="template-greeting" />
            </div>
            <div>
              <Label className="font-body text-sm">Button Text</Label>
              <Input value={form.button_text} onChange={e => handleChange('button_text', e.target.value)} className="font-body mt-1" data-testid="template-button-text" />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="font-body text-sm">Body Text</Label>
              <Textarea value={form.body_text} onChange={e => handleChange('body_text', e.target.value)} className="font-body mt-1 min-h-[100px]" data-testid="template-body-text" />
            </div>
            <div>
              <Label className="font-body text-sm">Footer Text</Label>
              <Textarea value={form.footer_text} onChange={e => handleChange('footer_text', e.target.value)} className="font-body mt-1" data-testid="template-footer-text" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm">Primary Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form.primary_color} onChange={e => handleChange('primary_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="template-primary-color" />
                  <Input value={form.primary_color} onChange={e => handleChange('primary_color', e.target.value)} className="font-mono text-xs flex-1" />
                </div>
              </div>
              <div>
                <Label className="font-body text-sm">Accent Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form.accent_color} onChange={e => handleChange('accent_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="template-accent-color" />
                  <Input value={form.accent_color} onChange={e => handleChange('accent_color', e.target.value)} className="font-mono text-xs flex-1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-white" data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Template
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={resetting} data-testid="reset-template-btn">
            {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Reset to Default
          </Button>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-heading">Email Preview</DialogTitle>
            <DialogDescription className="font-body">Preview with sample data</DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh] border rounded-lg bg-card">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await emailTemplatesAPI.getAll();
      setTemplates(res.data);
    } catch {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleSave = (updated) => {
    setTemplates(prev => prev.map(t => t.template_type === updated.template_type ? { ...t, ...updated } : t));
    setEditing(prev => prev ? { ...prev, ...updated } : prev);
  };

  const handleReset = (reset) => {
    setTemplates(prev => prev.map(t => t.template_type === reset.template_type ? { ...t, ...reset } : t));
    setEditing(prev => prev ? { ...prev, ...reset } : prev);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Palette className="w-6 h-6 text-primary" />
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
              Email Templates
            </h1>
          </div>
          <p className="font-body text-muted-foreground">
            Customize the subject, content, and branding of all outgoing emails
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : editing ? (
          <EditPanel
            template={editing}
            onSave={handleSave}
            onReset={handleReset}
            onClose={() => setEditing(null)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map(t => (
              <TemplateCard key={t.template_type} template={t} onEdit={setEditing} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
